import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../../app/store';
import { CloudFirmwareStatus, FirmwareUpdateStatus } from '../ev-charger/types';
import { SmartAmpSseState } from './useSmartAmpSse';

export type { CloudFirmwareStatus, FirmwareUpdateStatus };

export type EvseStreamState = SmartAmpSseState & {
  amountUsage: number | null;
  currency: string | null;
  kwhConsumed: number;
  kwInstantaneousPower: number | null;
  maxAmps: number | null;
  chargingAmps: number | null;
  hasReceivedSseEvent: boolean;
  /** Granular OCPP-level step (`firmwareStatus` from the SSE event). `null` when no
   *  update is in progress. */
  firmwareUpdateStatus: FirmwareUpdateStatus | null;
  /** High-level aggregate status (`cloudFirmwareStatus` from the SSE event).
   *  Drives top-level UI pending banner. */
  cloudFirmwareStatus: CloudFirmwareStatus | null;
  /** Firmware version string from the last `FirmwareUpdateStatus` event.
   *  Used in the success toast copy ("Firmware updated to v{firmwareVersion}"). */
  firmwareVersion: string | null;
};

export const initialState: EvseStreamState = {
  loadActive: false,
  tempActive: false,
  status: 'inactive',
  amountUsage: null,
  currency: null,
  kwhConsumed: 0,
  kwInstantaneousPower: null,
  maxAmps: null,
  chargingAmps: null,
  hasReceivedSseEvent: false,
  cloudFirmwareStatus: null,
  firmwareUpdateStatus: null,
  firmwareVersion: null,
};

export const evseStreamSlice = createSlice({
  name: 'evseStream',
  initialState,
  reducers: {
    setSmartAmpState: (state, action: PayloadAction<SmartAmpSseState>) => {
      return { ...state, ...action.payload };
    },
    setMeterValues: (
      state,
      action: PayloadAction<{
        amountUsage: number | null;
        currency: string | null;
        kwhConsumed: number;
        kwInstantaneousPower: number | null;
        maxAmps?: number | null;
        chargingAmps?: number | null;
      }>
    ) => {
      return {
        ...state,
        amountUsage: action.payload.amountUsage,
        currency: action.payload.currency,
        kwhConsumed: action.payload.kwhConsumed,
        kwInstantaneousPower: action.payload.kwInstantaneousPower,
        maxAmps: action.payload.maxAmps ?? null,
        chargingAmps: action.payload.chargingAmps ?? null,
        hasReceivedSseEvent: true,
      };
    },
    resetMeterValues: (state) => {
      return {
        ...state,
        amountUsage: null,
        currency: null,
        kwInstantaneousPower: null,
        maxAmps: null,
        chargingAmps: null,
      };
    },
    setKwhConsumed: (state, action: PayloadAction<number>) => {
      return { ...state, kwhConsumed: action.payload };
    },
    setAmps: (state, action: PayloadAction<{ maxAmps: number | null; chargingAmps: number | null }>) => {
      return { ...state, ...action.payload };
    },
    // Zero (not null) so screens don't fall back to stale REST power/current; maxAmps is a rating and is kept.
    clearLiveChargingReadings: (state) => {
      return { ...state, kwInstantaneousPower: 0, chargingAmps: 0 };
    },
    resetSmartAmpState: () => initialState,
    setFirmwareUpdateStatus: (
      state,
      action: PayloadAction<{
        status: FirmwareUpdateStatus | null;
        cloudStatus?: CloudFirmwareStatus | null;
        firmwareVersion?: string | null;
      }>
    ) => {
      return {
        ...state,
        firmwareUpdateStatus: action.payload.status,
        ...(action.payload.cloudStatus !== undefined && {
          cloudFirmwareStatus: action.payload.cloudStatus,
        }),
        ...(action.payload.firmwareVersion !== undefined && { firmwareVersion: action.payload.firmwareVersion }),
      };
    },
  },
});

export const {
  setSmartAmpState,
  setMeterValues,
  resetMeterValues,
  setKwhConsumed,
  setAmps,
  clearLiveChargingReadings,
  resetSmartAmpState,
  setFirmwareUpdateStatus,
} = evseStreamSlice.actions;

export const selectSmartAmpData = (state: RootState) => state.evseStream;
export const selectMeterValues = createSelector(
  (state: RootState) => state.evseStream.amountUsage,
  (state: RootState) => state.evseStream.currency,
  (state: RootState) => state.evseStream.kwhConsumed,
  (state: RootState) => state.evseStream.kwInstantaneousPower,
  (state: RootState) => state.evseStream.maxAmps,
  (state: RootState) => state.evseStream.chargingAmps,
  (amountUsage, currency, kwhConsumed, kwInstantaneousPower, maxAmps, chargingAmps) => ({
    amountUsage,
    currency,
    kwhConsumed,
    kwInstantaneousPower,
    maxAmps,
    chargingAmps,
  })
);

export const selectHasReceivedSseEvent = (state: RootState) => state.evseStream.hasReceivedSseEvent;

export const selectFirmwareUpdateStatus = createSelector(
  (state: RootState) => state.evseStream.firmwareUpdateStatus,
  (state: RootState) => state.evseStream.cloudFirmwareStatus,
  (state: RootState) => state.evseStream.firmwareVersion,
  (firmwareUpdateStatus, cloudFirmwareStatus, firmwareVersion) => ({
    firmwareUpdateStatus,
    cloudFirmwareStatus,
    firmwareVersion,
  })
);

export const { reducer: evseStreamReducer } = evseStreamSlice;
